import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as yup from 'yup';

import { datasourceEndpoints } from '@/api/endpoints';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDatasources from '@/hooks/useDatasources';
import { setDatasourceId } from '@/redux/session/actions';
import { selectDatasourceId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { useHasDirtyFields } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type FormData = { name: yup.InferType<ReturnType<typeof createNameFieldSchema>> };

export const RenameFolderForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const { collectionId } = useParams();
  const datasourceId = useSelector(selectDatasourceId);

  const { datasource, updateDatasource } = useDatasources(collectionId, datasourceId, true);

  // Create schema with dynamic name validation
  const formSchema = yup.object({
    name: createNameFieldSchema({
      getEndpoint: (name) =>
        datasourceEndpoints.checkName(collectionId, name, { datasource_id: datasourceId }),
      max: LENGTH_XS,
      excludeName: datasource?.name,
    }),
  });

  type FormValues = yup.InferType<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: '' },
  });

  const isDirty = useHasDirtyFields(form.control);

  useEffect(() => {
    if (datasource) {
      form.reset({ name: datasource.name });
    }
  }, [datasource, form]);

  const handleClose = () => {
    dispatch(setSidebarContent(null));
    dispatch(setDatasourceId(null));
  };

  const handleSubmit = (data: FormData) => {
    // A missing datasource (stale selection, 404) would throw on `datasource.type` below.
    if (!datasource) {
      return;
    }
    updateDatasource(
      { ...datasource, name: data?.name, datasource_id: datasourceId, type: datasource.type },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('collections.rename_folder')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <div className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.name')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="w-full"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        autoFocus
                        onChange={(e) => {
                          field.onChange(e);
                          form.trigger('name');
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="mt-4 flex flex-row-reverse items-center gap-2">
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || !isDirty || !datasource}
                >
                  {t('common.rename')}
                </Button>

                <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            setIsUnsavedChangesModalOpen(false);
            dispatch(setSidebarContent(null));
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
